import { Box } from "@mui/material";
import React, { CSSProperties, useState } from "react";

import { MAX_ANNOTATIONS_PER_IQ } from "config/constants";
import { useBoundingBoxesContext } from "context/boundingBoxesContext";
import { useImageSettingsContext } from "context/ImageSettingsContext/imageSettingsContext";
import { AnswerPanelNew } from "features/answerPanel/AnswerPanelNew";
import { Canvas } from "features/canvas/Canvas";
import { CanvasToolbar } from "features/canvas/components/CanvasToolbar";
import { Point, Size } from "features/canvas/types";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { DisplayBBoxInfo, ImageQuery } from "types/imageQueries";
import { LabelValue } from "types/labelValue";
import { generateImageUrl } from "utils/generateImageUrl";

type GroundlightImageQueryContentProps = {
  data?: ImageQuery;
  detector?: Detector;
  labelRows?: LabelValue[];
  loading?: boolean;
  onSendLabel?: () => Promise<void>;
  style?: CSSProperties;
  enableAnnotation?: boolean;
  enablePanZoom?: boolean;
  hideToolbar?: boolean;
  hideAnswerPanel?: boolean;
  title?: string;
  imageData?: [undefined | HTMLImageElement, "loaded" | "loading" | "failed"];
  reviewMode?: boolean;
  reviewBBoxInfo?: DisplayBBoxInfo;
  selectedLabelValue?: LabelValue | null;
};

export function GroundlightImageQueryContent({
  enablePanZoom,
  style,
  enableAnnotation,
  data,
  detector,
  onSendLabel,
  loading = false,
  labelRows,
  hideToolbar,
  hideAnswerPanel,
  title,
  imageData,
  reviewMode = false,
  selectedLabelValue,
  reviewBBoxInfo,
}: GroundlightImageQueryContentProps) {
  const {
    annotations,
    setAnnotations,
    actualBox,
    selectedAnnotation,
    setSelectedAnnotation,
    showAnnotations,
    validBoxLabels,
    stepsData,
  } = useBoundingBoxesContext();

  const { isEditing } = useImageSettingsContext();
  const [boxContainerSize, setBoxContainerSize] = useState<Size>({ width: 0, height: 0 });
  const [position, setPosition] = useState<Point>({ x: 0, y: 0 });

  const fullEnableAnnotation = enableAnnotation && stepsData.currentStep <= validBoxLabels.length;
  const fullCanInteract = isEditing && fullEnableAnnotation;

  const maxBoundingBoxes = () => {
    if (detector?.mode === DetectorModes.BOUNDING_BOX) {
      return detector.mode_configuration?.max_num_bboxes ?? MAX_ANNOTATIONS_PER_IQ;
    }
    if (detector?.mode === DetectorModes.COUNT) {
      return detector?.mode_configuration?.max_count ?? MAX_ANNOTATIONS_PER_IQ;
    }
    return MAX_ANNOTATIONS_PER_IQ;
  };

  return (
    <Box
      id="groundlight-image-query-content"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {!hideToolbar && (
        <CanvasToolbar
          title={title}
          boxContainerSize={boxContainerSize}
          position={position}
          setPosition={setPosition}
        />
      )}

      <Canvas
        style={style}
        iqId={data?.iq_id ?? ""}
        imageUrl={data ? generateImageUrl(data.iq_id) : ""}
        imageData={imageData}
        showBoundingBoxes={showAnnotations}
        enablePanZoom={enablePanZoom}
        enableAnnotation={fullEnableAnnotation}
        annotations={annotations}
        setAnnotations={setAnnotations}
        selectedAnnotation={selectedAnnotation}
        setSelectedAnnotation={setSelectedAnnotation}
        currentBox={actualBox}
        canInteract={fullCanInteract}
        maxBoundingBoxes={maxBoundingBoxes() ?? 0}
        boxContainerSize={boxContainerSize}
        setBoxContainerSize={setBoxContainerSize}
        position={position}
        setPosition={setPosition}
      />

      {!hideAnswerPanel && (
        <Box sx={{ zIndex: 1 }}>
          {detector && data && (
            <AnswerPanelNew
              selectedImageQuery={data}
              loading={loading}
              onSendLabel={onSendLabel}
              detector={detector}
              labelRows={labelRows}
              reviewMode={reviewMode}
              selectedLabelValue={selectedLabelValue}
              reviewBBoxInfo={reviewBBoxInfo}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
